/**
 * Format date range for display
 * @param {Date|null} startDate - Start date
 * @param {Date|null} endDate - End date
 * @returns {string} Formatted date range string
 */
export const formatDateRangeShort = (startDate, endDate) => {
  if (!startDate || !endDate) return 'Select Date Range';

  const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleString('en-US', { month: 'short' });

  // Show shorter format if dates are in same year
  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}, ${startDate.getFullYear()}`;
  }

  return `${startMonth} ${startDate.getDate()}, ${startDate.getFullYear()} - ${endMonth} ${endDate.getDate()}, ${endDate.getFullYear()}`;
};

/**
 * Format date for input field (From)
 * @param {Date|null} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  const month = date.toLocaleString('en-US', { month: 'short' });
  return `From ${date.getDate()} ${month}`;
};

/**
 * Format date for input field (To)
 * @param {Date|null} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForInputTo = (date) => {
  if (!date) return '';
  const month = date.toLocaleString('en-US', { month: 'short' });
  return `To ${date.getDate()} ${month}`;
};

/**
 * Get current month name
 * @returns {string} Current month name
 */
export const getCurrentMonth = () => {
  return new Date().toLocaleString('en-US', { month: 'long' });
};

/**
 * Format original date label from API to "MM-DD-YYYY (hour:min)" format
 * Handles formats like:
 * - "2026-01-03" -> "01-03-2026"
 * - "2026-01-03 20-24" -> "01-03-2026 (20:24)"
 * - "2026-01-03 18" -> "01-03-2026 (18:00)"
 * - "2025-12" -> "12-01-2025"
 * @param {string} label - Original date label from API
 * @returns {string} Formatted date string
 */
export const formatOriginalDateLabel = (label) => {
  if (!label || typeof label !== 'string') {
    return '';
  }

  // Handle HOUR formats: "2026-01-03 20-24" or "2026-01-03 18"
  const parts = label.split(' ');
  const datePart = parts[0] || label;
  const timePart = parts[1] || '';

  // Format time part: convert "20-24" to "20:24" or "18" to "18:00"
  let formattedTimePart = '';
  if (timePart) {
    if (timePart.includes('-')) {
      // Range format: "20-24" -> "20:24"
      formattedTimePart = timePart.replace(/-/g, ':');
    } else {
      // Single hour: "18" -> "18:00"
      formattedTimePart = `${timePart.padStart(2, '0')}:00`;
    }
  }

  // Handle MONTH format: "2025-12"
  if (datePart.match(/^\d{4}-\d{2}$/)) {
    // Parse as local date to avoid timezone issues
    const [year, month] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, 1); // month is 0-indexed
    if (isNaN(date.getTime())) {
      return label;
    }
    const monthStr = String(month).padStart(2, '0');
    const dayStr = '01'; // First day of month
    return formattedTimePart
      ? `${monthStr}-${dayStr}-${year} (${formattedTimePart})`
      : `${monthStr}-${dayStr}-${year}`;
  }

  // Handle DAY format: "2026-01-03"
  // Parse as local date to avoid timezone issues (UTC -> local conversion)
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  if (isNaN(date.getTime())) {
    return label;
  }

  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  const yearNum = date.getFullYear();

  return formattedTimePart
    ? `${monthStr}-${dayStr}-${yearNum} (${formattedTimePart})`
    : `${monthStr}-${dayStr}-${yearNum}`;
};

/**
 * Format date as "YYYY-MM-DD HH:mm" for API requests
 * @param {Date|string|null} date - Date object or formatted string to format
 * @returns {string|null} Formatted date string or null if date is invalid
 */
export const formatDateTimeString = (date) => {
  if (!date) return null;

  // If it's already a formatted string, return it as-is
  if (typeof date === 'string') {
    return date;
  }

  // If it's a Date object, format it
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  return null;
};
